/**
 * SpendingTrendChart
 *
 * 지출 추이 차트 (라인/막대)
 * 수입/지출/잔액을 개별적으로 선택하여 표시할 수 있음
 */

import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { useState, useEffect } from 'react'
import type { TimeSeriesPoint, ChartDataKey } from '../../types/Statistics'

interface SpendingTrendChartProps {
  data: TimeSeriesPoint[]
  onPointClick: (point: TimeSeriesPoint) => void
  visibleData: ChartDataKey[]
  onVisibleDataChange: (keys: ChartDataKey[]) => void
}

/**
 * 차트 색상 상수
 * Recharts는 CSS 변수를 직접 읽지 못하므로 색상값 하드코딩
 */
const CHART_COLORS = {
  income: '#10b981',   // Green 500
  expense: '#ef4444',  // Red 500
  net: '#2563eb'       // Blue 600
}

export function SpendingTrendChart({
  data,
  onPointClick,
  visibleData,
  onVisibleDataChange
}: SpendingTrendChartProps) {
  const [chartType, setChartType] = useState<'line' | 'bar'>('line')

  // 반응형 Y축 라벨 폰트 크기
  const [yAxisLabelFontSize, setYAxisLabelFontSize] = useState(13)

  useEffect(() => {
    const updateFontSize = () => {
      setYAxisLabelFontSize(window.innerWidth < 640 ? 11 : 13)
    }
    updateFontSize()
    window.addEventListener('resize', updateFontSize)
    return () => window.removeEventListener('resize', updateFontSize)
  }, [])

  /**
   * 체크박스 토글 핸들러
   * 선택된 데이터 키를 추가하거나 제거하고 부모 컴포넌트에 전달
   */
  const toggleDataKey = (key: ChartDataKey) => {
    const newVisibleData = visibleData.includes(key)
      ? visibleData.filter(k => k !== key)
      : [...visibleData, key]
    onVisibleDataChange(newVisibleData)
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4">
      <div className="flex flex-col gap-3 mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">
          지출 추이
        </h3>

        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* 데이터 선택 체크박스 */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={visibleData.includes('income')}
                onChange={() => toggleDataKey('income')}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer"
              />
              <span>수입</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={visibleData.includes('expense')}
                onChange={() => toggleDataKey('expense')}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer"
              />
              <span>지출</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-200">
              <input
                type="checkbox"
                checked={visibleData.includes('net')}
                onChange={() => toggleDataKey('net')}
                className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500 cursor-pointer"
              />
              <span>잔액</span>
            </label>
          </div>

          {/* 차트 타입 토글 */}
          <div className="flex gap-2">
            <button
              onClick={() => setChartType('line')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                chartType === 'line'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
              }`}
            >
              라인
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                chartType === 'bar'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
              }`}
            >
              막대
            </button>
          </div>
        </div>
      </div>

      {/* 빈 상태: 모든 체크박스 해제 시 */}
      {visibleData.length === 0 ? (
        <div className="h-[300px] flex items-center justify-center text-gray-500 dark:text-gray-400">
          <p>표시할 데이터를 선택해주세요</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          {chartType === 'line' ? (
            <LineChart
              data={data}
              onClick={(chartData) => chartData?.activePayload?.[0] && onPointClick(chartData.activePayload[0].payload)}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke="#ef4444"
                tick={{ fill: '#6b7280' }}
                tickFormatter={(value: number) => `${(value / 10000).toFixed(0)}만`}
                label={{
                  value: '지출',
                  angle: 0,
                  position: 'insideLeft',
                  style: {
                    fill: '#ef4444',
                    fontWeight: 'bold',
                    fontSize: yAxisLabelFontSize,
                    writingMode: 'vertical-rl'
                  },
                  offset: 10
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#10b981"
                tick={{ fill: '#6b7280' }}
                tickFormatter={(value: number) => `${(value / 10000).toFixed(0)}만`}
                label={{
                  value: '수입/잔액',
                  angle: 0,
                  position: 'insideRight',
                  style: {
                    fill: '#10b981',
                    fontWeight: 'bold',
                    fontSize: yAxisLabelFontSize,
                    writingMode: 'vertical-rl'
                  },
                  offset: 10
                }}
              />
              <Tooltip
                formatter={(value: number) => `${value.toLocaleString()}원`}
              />
              <Legend />
              {visibleData.includes('income') && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="income"
                  stroke={CHART_COLORS.income}
                  name="수입"
                  strokeWidth={2}
                />
              )}
              {visibleData.includes('expense') && (
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="expense"
                  stroke={CHART_COLORS.expense}
                  name="지출"
                  strokeWidth={2}
                />
              )}
              {visibleData.includes('net') && (
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="net"
                  stroke={CHART_COLORS.net}
                  name="잔액"
                  strokeWidth={2}
                />
              )}
            </LineChart>
          ) : (
            <BarChart
              data={data}
              onClick={(chartData) => chartData?.activePayload?.[0] && onPointClick(chartData.activePayload[0].payload)}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" />
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke="#ef4444"
                tick={{ fill: '#6b7280' }}
                tickFormatter={(value: number) => `${(value / 10000).toFixed(0)}만`}
                label={{
                  value: '지출',
                  angle: 0,
                  position: 'insideLeft',
                  style: {
                    fill: '#ef4444',
                    fontWeight: 'bold',
                    fontSize: yAxisLabelFontSize,
                    writingMode: 'vertical-rl'
                  },
                  offset: 10
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#10b981"
                tick={{ fill: '#6b7280' }}
                tickFormatter={(value: number) => `${(value / 10000).toFixed(0)}만`}
                label={{
                  value: '수입/잔액',
                  angle: 0,
                  position: 'insideRight',
                  style: {
                    fill: '#10b981',
                    fontWeight: 'bold',
                    fontSize: yAxisLabelFontSize,
                    writingMode: 'vertical-rl'
                  },
                  offset: 10
                }}
              />
              <Tooltip
                formatter={(value: number) => `${value.toLocaleString()}원`}
              />
              <Legend />
              {visibleData.includes('income') && (
                <Bar yAxisId="right" dataKey="income" fill={CHART_COLORS.income} name="수입" />
              )}
              {visibleData.includes('expense') && (
                <Bar yAxisId="left" dataKey="expense" fill={CHART_COLORS.expense} name="지출" />
              )}
              {visibleData.includes('net') && (
                <Bar yAxisId="right" dataKey="net" fill={CHART_COLORS.net} name="잔액" />
              )}
            </BarChart>
          )}
        </ResponsiveContainer>
      )}
    </div>
  )
}
